/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */
import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../../common/constants';
import utils from '../../../common/utils';

export default class ChatutilityBooksService extends BaseService {
  constructor() {
    super(constants.CHATUTILITY_MANAGE_BOOKS_DATA_FETCHED, 'chatutilitybooksStatus');
  }

  handleEvent(eventName, event) {
    const { searchParams, tenantId, status } = event;

    // Validate tenantId is required (backend now requires x-berlin-tenant-id header)
    if (!tenantId) {
      const notification = {
        open: true,
        message: 'Tenant ID is required. Please refresh the page and try again.',
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      return Promise.reject(new Error('Tenant ID is required'));
    }

    // Build headers with tenantId (required by backend)
    const headers = { [constants.BERLIN_TENANT_ID_HEADER]: tenantId };

    // Only use search endpoint if there's actual search text
    // Status-only searches will be handled on frontend after fetching all data
    if (searchParams && searchParams.trim()) {
      const searchValue = searchParams.trim();

      // API expects bookId (not identifierId)
      const requestBody = {
        bookId: searchValue,
        title: searchValue,
        author: searchValue
      };
      if (status && status.trim()) {
        requestBody.lifeCycleStatus = status.trim();
      }
      return this.getClient('aiadmin').post('/chatutility/titles/search', requestBody, { headers });
    }
    // Get all titles, optionally filter by status via query parameter
    let url = '/chatutility/titles';
    const config = { headers };
    if (status && status.trim()) {
      const queryParams = new URLSearchParams({ lifeCycleStatus: status.trim() });
      url = `${url}?${queryParams.toString()}`;
    }
    return this.getClient('aiadmin').get(url, config);
  }

  handleResponse(response) {
    let responseData = response.data?.data || response.data || [];
    if (!Array.isArray(responseData)) {
      responseData = [];
    }
    Framework.getStoreRegistry().getStore('chatutilitybooksState').set({ data: responseData });
  }

  handleError(error) {
    const { response } = error;
    if (response) {
      const { status, data } = response;
      if (status === 401) {
        utils.redirectToLogin();
      } else {
        let errorMessage = 'Failed to fetch titles. Please try again.';
        // Handle error response structure: { error: { code, message }, status: "failure" }
        if (data?.error) {
          if (typeof data.error === 'string') {
            errorMessage = data.error;
          } else if (data.error.message) {
            errorMessage = data.error.message;
          }
        } else if (data?.message) {
          errorMessage = data.message;
        }
        const notification = {
          open: true,
          type: 'error',
          message: errorMessage,
          duration: 5000
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
      }
    } else {
      // Network or other error
      const notification = {
        open: true,
        type: 'error',
        message: 'Network error. Please check your connection and try again.',
        duration: 5000
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      console.error('Titles search error:', error);
    }
  }
}
